import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { adminFetch } from '../utils/adminFetch';
import { getAdminCourseName } from '../utils/courseDisplay';
import MultilingualInput from '../components/MultilingualInput';
import MarkdownEditorMultilingual from '../components/MarkdownEditorMultilingual';
import {
  BATCH_STATUS_COLORS,
  BATCH_STATUS_LABELS,
  formatParisDateTime,
  minScheduleDatetimeLocal,
  parseDatetimeLocalValue,
  toDatetimeLocalValue,
} from '../utils/studentMessageBatches';

type BatchDetail = {
  id: string;
  courseId: string;
  courseTitle: string;
  courseProject?: string;
  status: string;
  scheduledAt: string;
  recipientCount: number;
  studentIds: string[];
  content: {
    subject: Record<string, string>;
    body: Record<string, string>;
    templateId?: string;
    templateKey?: string;
    includeMagicLink?: boolean;
    customButtonUrl?: string;
  };
  summary: {
    totalRecipients: number;
    sent: number;
    failed: number;
    skipped: number;
    rows: Array<{
      studentId: string;
      firstName: string;
      lastName: string;
      email: string;
      language: string;
      status: 'sent' | 'failed' | 'skipped';
      message: string;
    }>;
  } | null;
  errorMessage?: string | null;
};

type Recipient = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
};

type CourseStudent = {
  student: { id: string; firstName: string; lastName: string; email: string };
};

const ALL_LANGUAGES = ['en', 'fr', 'es', 'de', 'pt'];

const STATUS_LABELS = { sent: 'Envoyé', failed: 'Échec', skipped: 'Ignoré' } as const;
const STATUS_COLORS = { sent: 'success', failed: 'error', skipped: 'default' } as const;

const StudentMessageBatchDetail: React.FC = () => {
  const { batchId } = useParams<{ batchId: string }>();
  const navigate = useNavigate();
  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

  const [batch, setBatch] = useState<BatchDetail | null>(null);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [courseStudents, setCourseStudents] = useState<CourseStudent[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [editScheduledAt, setEditScheduledAt] = useState('');
  const [editSubject, setEditSubject] = useState<Record<string, string>>({});
  const [editBody, setEditBody] = useState<Record<string, string>>({});
  const [editStudentIds, setEditStudentIds] = useState<string[]>([]);

  const isEditable = batch?.status === 'scheduled';

  const availableLanguages = useMemo(() => {
    const langs = new Set<string>();
    ALL_LANGUAGES.forEach((lang) => {
      if (batch?.content.subject[lang] || batch?.content.body[lang]) langs.add(lang);
    });
    return langs.size > 0 ? Array.from(langs) : ['fr', 'en'];
  }, [batch]);

  const loadBatch = useCallback(async () => {
    if (!batchId) return;
    setLoading(true);
    setError('');
    try {
      const res = await adminFetch(`${apiBase}/student-messages/batches/${batchId}`);
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || data?.message || 'Lot introuvable');
      }
      const loaded: BatchDetail = data.batch;
      setBatch(loaded);
      setRecipients(data.recipients || []);
      setEditScheduledAt(toDatetimeLocalValue(new Date(loaded.scheduledAt)));
      setEditSubject(loaded.content.subject);
      setEditBody(loaded.content.body);
      setEditStudentIds(loaded.studentIds);

      if (loaded.status === 'scheduled') {
        const studentsRes = await adminFetch(`${apiBase}/courses/${loaded.courseId}/students`);
        const studentsData = await studentsRes.json();
        if (studentsRes.ok) {
          setCourseStudents(studentsData.students || []);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [apiBase, batchId]);

  useEffect(() => {
    void loadBatch();
  }, [loadBatch]);

  const toggleStudent = (studentId: string) => {
    setEditStudentIds((prev) =>
      prev.includes(studentId) ? prev.filter((id) => id !== studentId) : [...prev, studentId]
    );
  };

  const handleSave = async () => {
    if (!batchId || !batch) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await adminFetch(`${apiBase}/student-messages/batches/${batchId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scheduledAt: parseDatetimeLocalValue(editScheduledAt).toISOString(),
          studentIds: editStudentIds,
          subject: editSubject,
          body: editBody,
          includeMagicLink: batch.content.includeMagicLink !== false,
          customButtonUrl: batch.content.customButtonUrl,
          templateId: batch.content.templateId,
          templateKey: batch.content.templateKey,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || data?.message || 'Impossible de sauvegarder');
      }
      setSuccess('Lot mis à jour');
      await loadBatch();
    } catch (err: any) {
      setError(err.message || 'Erreur de sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    if (!batchId || !window.confirm('Annuler cet envoi planifié ?')) return;
    setSaving(true);
    setError('');
    try {
      const res = await adminFetch(`${apiBase}/student-messages/batches/${batchId}/cancel`, {
        method: 'POST',
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || data?.message || 'Impossible d\'annuler');
      }
      await loadBatch();
    } catch (err: any) {
      setError(err.message || 'Erreur d\'annulation');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!batch) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error">Lot introuvable</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/student-messages/batches')}
        sx={{ mb: 2 }}
      >
        Retour à la liste
      </Button>

      <Box mb={3} display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
        <Box>
          <Typography variant="h4" gutterBottom>
            Détail de l&apos;envoi
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {getAdminCourseName({ title: batch.courseTitle, courseProject: batch.courseProject })}
          </Typography>
        </Box>
        <Chip
          color={BATCH_STATUS_COLORS[batch.status] || 'default'}
          label={BATCH_STATUS_LABELS[batch.status] || batch.status}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      {batch.errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {batch.errorMessage}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={1}>
            <Typography variant="body2">
              <strong>Date prévue :</strong> {formatParisDateTime(batch.scheduledAt)}
            </Typography>
            <Typography variant="body2">
              <strong>Destinataires :</strong> {batch.recipientCount}
            </Typography>
            <Typography variant="body2">
              <strong>Lien Fasterclass :</strong>{' '}
              {batch.content.includeMagicLink !== false ? 'Oui (magic link)' : 'Non — URL personnalisée'}
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      {isEditable && (
        <>
          <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Modifier la planification
            </Typography>
            <TextField
              label="Date et heure d'envoi"
              type="datetime-local"
              fullWidth
              value={editScheduledAt}
              onChange={(e) => setEditScheduledAt(e.target.value)}
              inputProps={{ min: minScheduleDatetimeLocal() }}
              helperText="Heure locale du navigateur (Paris pour les admins en France). Minimum 15 minutes."
              sx={{ mb: 2 }}
            />
            <MultilingualInput
              label="Objet"
              value={editSubject}
              onChange={setEditSubject}
              availableLanguages={availableLanguages}
              required
            />
            <MarkdownEditorMultilingual
              label="Corps du message"
              value={editBody}
              onChange={setEditBody}
              availableLanguages={availableLanguages}
            />
          </Paper>

          {courseStudents.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Destinataires ({editStudentIds.length})
              </Typography>
              <TableContainer sx={{ maxHeight: 320 }}>
                <Table size="small" stickyHeader>
                  <TableBody>
                    {courseStudents.map((row) => (
                      <TableRow key={row.student.id}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={editStudentIds.includes(row.student.id)}
                            onChange={() => toggleStudent(row.student.id)}
                          />
                        </TableCell>
                        <TableCell>
                          {row.student.firstName} {row.student.lastName}
                        </TableCell>
                        <TableCell>{row.student.email}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          <Stack direction="row" spacing={1} mb={3}>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
              disabled={saving || editStudentIds.length === 0}
              onClick={() => void handleSave()}
            >
              Enregistrer
            </Button>
            <Button color="error" variant="outlined" disabled={saving} onClick={() => void handleCancel()}>
              Annuler l&apos;envoi
            </Button>
          </Stack>
        </>
      )}

      {!isEditable && recipients.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Destinataires
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Prénom</TableCell>
                  <TableCell>Nom</TableCell>
                  <TableCell>Email</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recipients.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>{r.firstName}</TableCell>
                    <TableCell>{r.lastName}</TableCell>
                    <TableCell>{r.email}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {batch.summary && (
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Rapport d&apos;envoi
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
            <Chip label={`Total : ${batch.summary.totalRecipients}`} />
            <Chip color="success" label={`Envoyés : ${batch.summary.sent}`} />
            {batch.summary.failed > 0 && <Chip color="error" label={`Échecs : ${batch.summary.failed}`} />}
            <Chip label={`Ignorés : ${batch.summary.skipped}`} />
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Prénom</TableCell>
                  <TableCell>Nom</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell>Détail</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {batch.summary.rows.map((row) => (
                  <TableRow key={`${row.studentId}-${row.email}`}>
                    <TableCell>{row.firstName}</TableCell>
                    <TableCell>{row.lastName}</TableCell>
                    <TableCell>{row.email}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={STATUS_COLORS[row.status]}
                        label={STATUS_LABELS[row.status]}
                      />
                    </TableCell>
                    <TableCell>{row.message}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Container>
  );
};

export default StudentMessageBatchDetail;
